import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  useProdutos,
  useCambio,
  useCotarFrete,
  useSalvarOrcamento,
  useAtualizarOrcamento,
} from '@/hooks/useOrcamentos';
import { usePlans } from '@/hooks/useSubscription';
import type { FreteOpcao, OrcamentoStatus } from '@/api/endpoints/orcamentos';
import { calcularOrcamento, brl, type KitItem } from './calc';
import { gerarPropostaPdf } from './proposta';
import { gerarComprovanteInterno } from './comprovante';
import { useOrcamentoDraft, type OrcamentoDraftData } from './draftStore';
import { Stepper, type StepDef } from './Stepper';
import { ResumoLateral } from './ResumoLateral';
import { ClienteStep } from './steps/ClienteStep';
import { KitStep } from './steps/KitStep';
import { SoftwareStep } from './steps/SoftwareStep';
import { FreteStep } from './steps/FreteStep';
import { RevisaoStep } from './steps/RevisaoStep';

// Valores que quase nunca mudam: ficam como premissa em texto no passo Revisão.
const PARAMS_PADRAO: OrcamentoDraftData['params'] = {
  cambio: 5.4,
  iiPct: 60,
  icmsPct: 17,
  freteIntlUsd: 0,
  margemPct: 40,
  freteNacional: 0,
  descontoBrincoPct: 0,
  descontoBrincoReais: 0,
};
const CLIENTE_VAZIO: OrcamentoDraftData['cliente'] = {
  nome: '',
  cidade: '',
  uf: '',
  cep: '',
  animais: '',
};
const SOFTWARE_PADRAO: OrcamentoDraftData['software'] = {
  plano: 'Plano personalizado',
  mensal: 0,
  meses: 12,
};
const INTERNO_PADRAO: OrcamentoDraftData['interno'] = { impostoVendaPct: 0, custoBrincoReais: 0 };

// Origem do frete: lembra a última usada neste navegador (quem viaja muda pouco).
const ORIGEM_PADRAO = { cep: '79002-000', local: 'Campo Grande, MS' };
const ORIGEM_KEY = 'orcamentos.origemFrete';
function origemSalva(): { cep: string; local: string } {
  try {
    const v = JSON.parse(localStorage.getItem(ORIGEM_KEY) ?? 'null');
    if (v && typeof v.cep === 'string' && typeof v.local === 'string') return v;
  } catch {
    // sem storage (aba privada etc.): fica no padrão
  }
  return ORIGEM_PADRAO;
}

const PASSOS = ['cliente', 'kit', 'software', 'frete', 'revisao'] as const;
type Passo = (typeof PASSOS)[number];

export function OrcamentoTab() {
  const produtosQ = useProdutos();
  const cambioQ = useCambio();
  const cotar = useCotarFrete();
  const planosQ = usePlans();
  const salvarMut = useSalvarOrcamento();
  const atualizarMut = useAtualizarOrcamento();
  const pending = useOrcamentoDraft((s) => s.pending);

  const produtos = useMemo(() => (produtosQ.data ?? []).filter((p) => p.ativo), [produtosQ.data]);
  const produtosById = useMemo(
    () => Object.fromEntries(produtos.map((p) => [p.id, p])),
    [produtos]
  );
  const planos = useMemo(() => (planosQ.data ?? []).filter((p) => p.ativo), [planosQ.data]);

  const [passo, setPassoState] = useState<Passo>('cliente');
  // Lado de onde o passo novo entra na animação (avançar ou voltar).
  const [direcao, setDirecao] = useState<'next' | 'prev'>('next');
  const setPasso = useCallback((p: Passo) => {
    setPassoState((atual) => {
      setDirecao(PASSOS.indexOf(p) >= PASSOS.indexOf(atual) ? 'next' : 'prev');
      return p;
    });
  }, []);
  const [cliente, setCliente] = useState(CLIENTE_VAZIO);
  const [params, setParams] = useState(PARAMS_PADRAO);
  const [software, setSoftware] = useState(SOFTWARE_PADRAO);
  const [planoId, setPlanoId] = useState<number | 'custom'>('custom');
  const [interno, setInterno] = useState(INTERNO_PADRAO);
  const [kit, setKit] = useState<{ produtoId: number; quantidade: number }[]>([]);
  const [orcamentoId, setOrcamentoId] = useState<number | undefined>(undefined);
  const [salvoOk, setSalvoOk] = useState(false);
  const [freteOpcoes, setFreteOpcoes] = useState<FreteOpcao[]>([]);
  const [freteConfigurado, setFreteConfigurado] = useState<boolean | null>(null);
  const [origem, setOrigemState] = useState(origemSalva);
  const cambioTouched = useRef(false);
  const hydratedNonce = useRef(0);

  // Qualquer edição tira o "Salvo" do botão.
  useEffect(() => {
    setSalvoOk(false);
  }, [cliente, params, software, planoId, interno, kit, origem]);

  // Câmbio ao vivo entra como padrão, a menos que tenha sido fixado à mão.
  useEffect(() => {
    if (cambioQ.data && !cambioTouched.current) {
      setParams((p) => ({ ...p, cambio: Number(cambioQ.data.toFixed(4)) }));
    }
  }, [cambioQ.data]);

  // Reabrir um orçamento salvo: reidrata tudo e vai direto pra revisão.
  useEffect(() => {
    if (!pending || pending.nonce === hydratedNonce.current) return;
    hydratedNonce.current = pending.nonce;
    const d = pending.data;
    setCliente(d.cliente);
    setParams(d.params);
    setSoftware(d.software);
    setPlanoId(d.planoId);
    setInterno(d.interno);
    setKit(d.kit);
    if (d.origem) setOrigemState(d.origem);
    setOrcamentoId(pending.id);
    setFreteOpcoes([]);
    setFreteConfigurado(null);
    setFreteKeyCotado(null);
    setPasso('revisao');
    cambioTouched.current = true; // respeita o câmbio salvo
  }, [pending, setPasso]);

  const kitItems: KitItem[] = useMemo(
    () =>
      kit
        .map((k) => ({ produto: produtosById[k.produtoId], quantidade: k.quantidade }))
        .filter((x): x is KitItem => Boolean(x.produto)),
    [kit, produtosById]
  );
  const resultado = useMemo(() => calcularOrcamento(kitItems, params), [kitItems, params]);
  const animais = Number(cliente.animais) || 0;
  const local = [cliente.cidade, cliente.uf].filter(Boolean).join(', ');

  // Nº de animais manda na quantidade de brincos (1 por animal).
  const setAnimais = (val: string) => {
    setCliente((c) => ({ ...c, animais: val }));
    const n = Number(val) || 0;
    if (n > 0) {
      setKit((k) =>
        k.map((it) =>
          produtosById[it.produtoId]?.categoria === 'brinco' ? { ...it, quantidade: n } : it
        )
      );
    }
  };

  const addProduto = (id: number) => {
    if (kit.some((k) => k.produtoId === id)) return;
    const qtd = produtosById[id]?.categoria === 'brinco' ? animais || 1 : 1;
    setKit((k) => [...k, { produtoId: id, quantidade: qtd }]);
  };
  const setQtd = (id: number, q: number) =>
    setKit((k) => k.map((it) => (it.produtoId === id ? { ...it, quantidade: q } : it)));
  const removeProduto = (id: number) => setKit((k) => k.filter((it) => it.produtoId !== id));

  const cepDigits = cliente.cep.replace(/\D/g, '');
  const podeCotar = cepDigits.length === 8 && kitItems.some((k) => k.quantidade > 0);

  // O que a cotação depende: destino, origem e itens/quantidades. Se mudar
  // depois de cotado, o valor fica velho: marca pra cotar de novo.
  const freteKey = JSON.stringify([cepDigits, origem.cep, kit]);
  const [freteKeyCotado, setFreteKeyCotado] = useState<string | null>(null);
  const freteDesatualizado = freteKeyCotado !== null && freteKeyCotado !== freteKey;
  useEffect(() => {
    if (freteDesatualizado) setFreteOpcoes([]);
  }, [freteDesatualizado]);

  const cotarMutate = cotar.mutate;
  const cotarFrete = useCallback(() => {
    const keyNaCotacao = freteKey;
    const itens = kitItems
      .filter((k) => k.quantidade > 0)
      .map((k) => ({
        id: k.produto.id,
        peso_g: k.produto.pesoG,
        comprimento_cm: k.produto.comprimentoCm,
        largura_cm: k.produto.larguraCm,
        altura_cm: k.produto.alturaCm,
        quantidade: k.quantidade,
      }));
    cotarMutate(
      { cepDestino: cliente.cep, cepOrigem: origem.cep, itens },
      {
        onSuccess: (r) => {
          setFreteKeyCotado(keyNaCotacao);
          setFreteConfigurado(r.configurado);
          setFreteOpcoes(r.opcoes);
          if (r.opcoes[0]) setParams((p) => ({ ...p, freteNacional: r.opcoes[0].preco }));
        },
      }
    );
  }, [kitItems, cliente.cep, origem.cep, cotarMutate, freteKey]);

  const setOrigem = (o: { cep: string; local: string }) => {
    setOrigemState(o);
    try {
      localStorage.setItem(ORIGEM_KEY, JSON.stringify(o));
    } catch {
      // storage indisponível: vale só pra esta sessão
    }
  };

  // Trocou a origem no passo Frete: cota de novo a partir dela.
  const origemAnterior = useRef(origem.cep);
  useEffect(() => {
    if (origemAnterior.current === origem.cep) return;
    origemAnterior.current = origem.cep;
    if (passo === 'frete' && podeCotar) cotarFrete();
  }, [origem.cep, passo, podeCotar, cotarFrete]);

  const montarPayload = (status: OrcamentoStatus) => ({
    clienteNome: cliente.nome,
    clienteCidade: cliente.cidade,
    clienteUf: cliente.uf,
    totalGeral: resultado.totalGeral,
    status,
    dados: { cliente, params, software, planoId, interno, kit, origem },
  });

  const salvar = (status: OrcamentoStatus = 'rascunho') => {
    const payload = montarPayload(status);
    const ok = () => setSalvoOk(true);
    if (orcamentoId) {
      atualizarMut.mutate({ id: orcamentoId, data: payload }, { onSuccess: ok });
    } else {
      salvarMut.mutate(payload, {
        onSuccess: (o) => {
          setOrcamentoId(o.id);
          ok();
        },
      });
    }
  };
  const salvando = salvarMut.isPending || atualizarMut.isPending;

  const gerarProposta = () => {
    salvar('enviado'); // toda proposta enviada vai pro histórico
    gerarPropostaPdf({
      cliente,
      itens: kitItems,
      resultado,
      softwarePlano: software.plano,
      softwareMensal: software.mensal,
      softwareMeses: software.meses,
      prazoEntrega: '',
      validadeDias: 15,
      garantiaMeses: 12,
    });
  };

  const gerarComprovante = () =>
    gerarComprovanteInterno({
      cliente,
      itens: kitItems,
      resultado,
      softwarePlano: software.plano,
      softwareMensal: software.mensal,
      softwareMeses: software.meses,
      impostoVendaPct: interno.impostoVendaPct,
      custoBrincoReais: interno.custoBrincoReais,
    });

  const novo = () => {
    if (!salvoOk && kit.length > 0 && !window.confirm('Descartar o orçamento atual sem salvar?')) {
      return;
    }
    setCliente(CLIENTE_VAZIO);
    setParams((p) => ({ ...PARAMS_PADRAO, cambio: p.cambio }));
    setSoftware(SOFTWARE_PADRAO);
    setPlanoId('custom');
    setInterno(INTERNO_PADRAO);
    setKit([]);
    setOrcamentoId(undefined);
    setFreteOpcoes([]);
    setFreteConfigurado(null);
    setFreteKeyCotado(null);
    setPasso('cliente');
  };

  const steps: StepDef[] = [
    {
      id: 'cliente',
      label: 'Cliente',
      summary: cliente.nome
        ? [cliente.nome, animais ? `${animais.toLocaleString('pt-BR')} animais` : '']
            .filter(Boolean)
            .join(' · ')
        : 'Nome, rebanho e CEP',
      done: Boolean(cliente.nome && animais > 0 && cepDigits.length === 8),
    },
    {
      id: 'kit',
      label: 'Kit',
      summary: kitItems.length
        ? `${kitItems.length} ${kitItems.length === 1 ? 'item' : 'itens'}`
        : 'Leitor, brincos, aplicador',
      done: kitItems.length > 0,
    },
    {
      id: 'software',
      label: 'Software',
      summary: software.mensal > 0 ? software.plano : 'Opcional',
      done: software.mensal > 0,
    },
    {
      id: 'frete',
      label: 'Frete',
      summary: freteDesatualizado
        ? 'Cotar de novo'
        : params.freteNacional > 0
          ? brl(params.freteNacional)
          : 'A definir',
      done: params.freteNacional > 0 && !freteDesatualizado,
    },
    {
      id: 'revisao',
      label: 'Revisão',
      summary: kitItems.length ? brl(resultado.totalGeral) : 'Proposta e PDF',
      done: Boolean(salvoOk && orcamentoId),
    },
  ];

  const idx = PASSOS.indexOf(passo);
  const irPara = (i: number) => setPasso(PASSOS[Math.max(0, Math.min(PASSOS.length - 1, i))]);

  return (
    <div className="flex flex-col gap-5">
      <Stepper steps={steps} current={passo} onSelect={(id) => setPasso(id as Passo)} />

      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <section className="overflow-hidden rounded-lg border border-border bg-card p-5 sm:p-6">
          <div
            key={passo}
            className={direcao === 'next' ? 'animate-step-next' : 'animate-step-prev'}
          >
            {passo === 'cliente' && (
              <ClienteStep cliente={cliente} setCliente={setCliente} onAnimaisChange={setAnimais} />
            )}
            {passo === 'kit' && (
              <KitStep
                produtos={produtos}
                loading={produtosQ.isLoading}
                kit={kit}
                animais={animais}
                desconto={{ pct: params.descontoBrincoPct, reais: params.descontoBrincoReais }}
                resultado={resultado}
                onAdd={addProduto}
                onRemove={removeProduto}
                onQtd={setQtd}
                onDescontoChange={(d) =>
                  setParams((p) => ({
                    ...p,
                    descontoBrincoPct: d.pct,
                    descontoBrincoReais: d.reais,
                  }))
                }
              />
            )}
            {passo === 'software' && (
              <SoftwareStep
                planos={planos}
                planoId={planoId}
                software={software}
                onChange={(id, s) => {
                  setPlanoId(id);
                  setSoftware(s);
                }}
              />
            )}
            {passo === 'frete' && (
              <FreteStep
                origem={origem}
                onOrigemChange={setOrigem}
                cep={cliente.cep}
                local={local}
                pesoKg={resultado.pesoTotalKg}
                podeCotar={podeCotar}
                opcoes={freteOpcoes}
                configurado={freteConfigurado}
                cotando={cotar.isPending}
                erro={cotar.isError}
                valor={params.freteNacional}
                onCotar={cotarFrete}
                onValor={(v) => setParams((p) => ({ ...p, freteNacional: v }))}
              />
            )}
            {passo === 'revisao' && (
              <RevisaoStep
                params={params}
                setParams={(p) => {
                  if (p.cambio !== params.cambio) cambioTouched.current = true;
                  setParams(p);
                }}
                cambioAoVivo={!cambioTouched.current}
                cambioAtualizando={cambioQ.isFetching}
                onAtualizarCambio={() => {
                  cambioTouched.current = false;
                  cambioQ.refetch();
                }}
                interno={interno}
                setInterno={setInterno}
                resultado={resultado}
                softwarePlano={software.plano}
                softwareMensal={software.mensal}
                softwareMeses={software.meses}
                podeGerar={kitItems.length > 0}
                freteDesatualizado={freteDesatualizado}
                onRecotarFrete={() => setPasso('frete')}
                onGerarProposta={gerarProposta}
                onGerarComprovante={gerarComprovante}
              />
            )}
          </div>

          <div className="mt-8 flex items-center justify-between border-t border-border pt-4">
            {idx > 0 ? (
              <Button variant="ghost" onClick={() => irPara(idx - 1)}>
                <ArrowLeft size={16} /> {steps[idx - 1].label}
              </Button>
            ) : (
              <span />
            )}
            {idx < PASSOS.length - 1 && (
              <Button onClick={() => irPara(idx + 1)}>
                Continuar para {steps[idx + 1].label.toLowerCase()} <ArrowRight size={16} />
              </Button>
            )}
          </div>
        </section>

        <ResumoLateral
          clienteNome={cliente.nome}
          local={local}
          itens={kitItems}
          resultado={resultado}
          softwarePlano={software.plano}
          softwareMensal={software.mensal}
          softwareMeses={software.meses}
          salvando={salvando}
          salvoOk={salvoOk}
          editando={Boolean(orcamentoId)}
          podeSalvar={kitItems.length > 0}
          onSalvar={() => salvar('rascunho')}
          onNovo={novo}
        />
      </div>
    </div>
  );
}
